import * as React from 'react'

import { classList } from 'react-classlist-helper'

import {
  IResourceChipItem,
  ResourceChipList,
} from '~/client/src/shared/components/ResourceChipList'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import BaseStruxhubInput, { ISharedProps } from '../BaseStruxhubInput'
import CaretDownSelectorIcon from './CaretDownSelectorIcon'

// localization: translated

interface IProps extends ISharedProps {
  items: IResourceChipItem[]
  onClick?: () => void
  onSelectExisting?: () => void
  onRemove?: (id: string) => void
  onItemClick?: (id: string) => void
  isViewMode?: boolean
  renderIcon?: (item: IResourceChipItem) => React.ReactNode
  placeholderText?: string
  allowSelectExisting?: boolean
  /** When true, constrains height to a single row (no vertical growth). */
  isSingleValue?: boolean
  /** Shown in place of the chips, e.g. a location with its LBS. */
  valueContent?: React.ReactNode
}

const StruxhubResourceSelector: React.FC<IProps> = (props: IProps) => {
  const {
    items,
    onClick,
    onSelectExisting,
    onRemove,
    onItemClick,
    isViewMode,
    renderIcon,
    placeholderText,
    isReadOnly,
    isDisabled,
    allowSelectExisting = true,
    isSingleValue,
    valueContent,
  } = props

  const handleClick = React.useCallback(() => {
    if (isReadOnly || isDisabled) return

    if (allowSelectExisting && onSelectExisting) {
      onSelectExisting()
    } else if (onClick) {
      onClick()
    }
  }, [isReadOnly, isDisabled, allowSelectExisting, onSelectExisting, onClick])

  const isEmpty = !items || items.length === 0
  const hasChips = !isEmpty && !valueContent

  const placeholder = placeholderText || Localization.translator.select

  return (
    <BaseStruxhubInput {...props}>
      {isValueInvalid => (
        <div
          className={classList({
            'tw:cursor-pointer tw:bg-background tw:outline-none tw:rounded tw:w-full tw:relative': true,
            'tw:flex tw:items-center tw:py-[7px] tw:pl-2 tw:pr-6': !hasChips,
            'tw:py-1 tw:pl-1 tw:pr-6': hasChips,
            'tw:border tw:border-solid tw:border-border': !isValueInvalid,
            'tw:border tw:border-solid tw:border-destructive': isValueInvalid,
            // valueContent can stack a second line, so it grows like the multi-value box.
            'tw:h-10': isEmpty || (isSingleValue && !valueContent),
            'tw:min-h-10': !isEmpty && (!isSingleValue || !!valueContent),
          })}
          onClick={!isViewMode ? handleClick : undefined}
        >
          {isEmpty ? (
            <span className="tw:text-body-lg tw:truncate tw:text-muted-foreground">
              {placeholder}
            </span>
          ) : valueContent ? (
            <div className="tw:min-w-0 tw:overflow-hidden">{valueContent}</div>
          ) : (
            <ResourceChipList
              items={items}
              onRemove={onRemove}
              onItemClick={onItemClick}
              isViewMode={isViewMode}
              renderIcon={renderIcon}
            />
          )}
          <CaretDownSelectorIcon
            onClick={!isViewMode ? handleClick : undefined}
          />
        </div>
      )}
    </BaseStruxhubInput>
  )
}

export default StruxhubResourceSelector
